import { updateProfile } from 'firebase/auth';
import { collection, query, where, getDocs, Timestamp, doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User, UserStats, Subscription, GradeLevel, DifficultyLevel } from '../models/User';

/**
 * Updates the user's display name
 */
export async function updateUserDisplayName(user: any, displayName: string): Promise<void> {
  await updateProfile(user, { displayName });
}

/**
 * Fetches user statistics including study sessions and achievements
 */
export async function getUserStats(userId: string): Promise<UserStats> {
  const sessionsQuery = query(
    collection(db, 'studySessions'),
    where('userId', '==', userId)
  );
  const sessionsSnapshot = await getDocs(sessionsQuery);
  const sessions = sessionsSnapshot.docs.map(doc => doc.data());

  const totalSessions = sessions.length;
  const totalCards = sessions.reduce((sum, session) => sum + session.totalCards, 0);
  const totalCorrect = sessions.reduce((sum, session) => sum + session.correctCount, 0);
  const averageAccuracy = totalCards > 0 ? (totalCorrect / totalCards) * 100 : 0;
  
  const lastStudyDate = sessions.length > 0
    ? (sessions[sessions.length - 1].date as Timestamp).toDate()
    : null;

  const perfectSessions = sessions.filter(session => 
    session.correctCount === session.totalCards && session.totalCards > 0
  ).length;

  return {
    totalSessions,
    totalCards,
    averageAccuracy,
    lastStudyDate,
    perfectSessions,
  };
}

/**
 * Fetches the full Firestore user document
 */
export async function getUserDocument(uid: string): Promise<User | null> {
  const userRef = doc(db, 'users', uid);
  const snapshot = await getDoc(userRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as User;
}

/**
 * Updates grade and difficulty level preferences on the user document
 */
export async function updateUserLevelPreferences(
  uid: string,
  gradeLevel: GradeLevel,
  difficultyLevel: DifficultyLevel
): Promise<void> {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, { gradeLevel, difficultyLevel });
}

/**
 * Creates a new user document in Firestore
 */
export async function createUserDocument(
  uid: string,
  email: string,
  displayName: string,
  termsAccepted: boolean,
  privacyAccepted: boolean,
  aiCardGeneration?: {
    dailyLimit: number;
    cardsGeneratedToday: number;
    lastGenerationDate: string;
  },
  gradeLevel?: GradeLevel,
  difficultyLevel?: DifficultyLevel
): Promise<void> {
  const now = Timestamp.now();
  
  // Create initial subscription data
  const initialSubscription: Subscription = {
    tier: 'free',
    status: 'active',
    validUntil: new Timestamp(now.seconds + (30 * 24 * 60 * 60), 0), // 30 days from now
    lastChecked: now,
    features: ['basic_study', 'history'], // Basic features available to free tier
    paymentProvider: 'internal',
    priceId: null,
    stripeSubscriptionId: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    trialEndsAt: null,
  };

  const userData: Omit<User, 'id'> = {
    email,
    displayName,
    termsAccepted,
    privacyAccepted,
    createdAt: now,
    updatedAt: now,
    lastLoginAt: now,
    subscription: initialSubscription,
    hasUsedTrial: false,
    ...(aiCardGeneration && { aiCardGeneration }),
    ...(gradeLevel && { gradeLevel }),
    ...(difficultyLevel && { difficultyLevel }),
  };

  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, userData);
} 